/**
 * Mini-Markdown-Renderer, wie im Mockup (`md()`-Funktion) — als React-Komponente statt
 * HTML-String, damit Dokumentinhalte nie über dangerouslySetInnerHTML laufen.
 */
import type { ReactNode } from "react";

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(https?:\/\/[^\s)]+)/g;
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    if (match[1]) nodes.push(<b key={key++}>{match[2]}</b>);
    else if (match[3]) nodes.push(<i key={key++}>{match[4]}</i>);
    else if (match[5]) nodes.push(<code key={key++}>{match[6]}</code>);
    else if (match[7])
      nodes.push(
        <a key={key++} href={match[7]} target="_blank" rel="noopener noreferrer">
          {match[7]}
        </a>,
      );
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function Markdown({ src }: { src: string }) {
  const lines = src.split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { type: "ul" | "ol"; items: string[] } | null = null;
  let key = 0;

  const flushPara = () => {
    if (para.length) {
      blocks.push(<p key={key++}>{inline(para.join(" "))}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const items = list.items;
      blocks.push(
        list.type === "ul" ? (
          <ul key={key++}>
            {items.map((it, i) => (
              <li key={i}>{inline(it)}</li>
            ))}
          </ul>
        ) : (
          <ol key={key++}>
            {items.map((it, i) => (
              <li key={i}>{inline(it)}</li>
            ))}
          </ol>
        ),
      );
      list = null;
    }
  };

  for (const raw of lines) {
    const h = raw.match(/^(#{1,3})\s+(.*)/);
    if (h) {
      flushPara();
      flushList();
      const level = h[1].length;
      const content = inline(h[2]);
      blocks.push(
        level === 1 ? <h1 key={key++}>{content}</h1> : level === 2 ? <h2 key={key++}>{content}</h2> : <h3 key={key++}>{content}</h3>,
      );
      continue;
    }
    const li = raw.match(/^[-*]\s+(.*)/);
    const oli = raw.match(/^\d+[.)]\s+(.*)/);
    if (li || oli) {
      flushPara();
      const want: "ul" | "ol" = li ? "ul" : "ol";
      if (!list || list.type !== want) {
        flushList();
        list = { type: want, items: [] };
      }
      list.items.push((li ?? oli)![1]);
      continue;
    }
    if (!raw.trim()) {
      flushPara();
      flushList();
      continue;
    }
    para.push(raw.trim());
  }
  flushPara();
  flushList();

  return <div className="md">{blocks}</div>;
}
